import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowDownCircle, ArrowUpCircle, ArrowRight,
    CheckCircle2, Clock, AlertCircle, CalendarCheck,
    Users, Building2, Eye, EyeOff,
} from 'lucide-react';
import { Card } from '@components/ui';
import clsx from 'clsx';
import { StackedBar, StatusRow, FinansalGrafik } from '../shared';

/**
 * GenelBakis — Genel Bakış Sekmesi
 * Filtreleme: Görünüm seçimi (grafik, tahsilat, alacak/borç bölümlerini aç/kapa)
 */
export default function GenelBakis({ summary, receivables, payables, formatCurrency, formatCompact, formatDate }) {
    const navigate = useNavigate();

    // Bölüm görünürlük filtreleri
    const [showSections, setShowSections] = useState({
        chart: true,
        collection: true,
        receivables: true,
    });

    const toggleSection = (key) => setShowSections(prev => ({ ...prev, [key]: !prev[key] }));

    // Calculations
    const totalCollections = (summary.collections?.collected || 0) + (summary.collections?.pending || 0) + (summary.collections?.overdue || 0);
    const totalPayments = (summary.payments?.paid || 0) + (summary.payments?.pending || 0) + (summary.payments?.scheduled || 0);
    const safePct = (val, total) => total > 0 ? Math.round((val / total) * 100) : 0;

    return (
        <div className="space-y-6">
            {/* Section Toggle Filters */}
            <div className="flex flex-wrap gap-2">
                {[
                    { key: 'chart', label: 'Trend Grafiği' },
                    { key: 'collection', label: 'Tahsilat / Ödeme' },
                    { key: 'receivables', label: 'Alacak / Borç' },
                ].map(sec => (
                    <button key={sec.key} onClick={() => toggleSection(sec.key)}
                        className={clsx("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border",
                            showSections[sec.key]
                                ? "border-primary/30 bg-primary/5 text-primary"
                                : "border-border-light bg-bg-tertiary text-text-muted hover:text-text-secondary"
                        )}>
                        {showSections[sec.key] ? <Eye size={12} /> : <EyeOff size={12} />}
                        {sec.label}
                    </button>
                ))}
            </div>

            {/* Trend Grafiği */}
            {showSections.chart && (
                <FinansalGrafik monthlyData={summary.monthlyBreakdown} formatCurrency={formatCurrency} />
            )}

            {/* Tahsilat & Ödeme Durumu */}
            {showSections.collection && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card padding="md" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
                                <Users size={18} className="text-text-muted" /> Tahsilat Durumu
                            </h3>
                            <span className="text-sm text-text-tertiary">{formatCompact(totalCollections)}</span>
                        </div>
                        <StackedBar segments={[
                            { color: 'bg-success', pct: safePct(summary.collections?.collected, totalCollections) },
                            { color: 'bg-warning', pct: safePct(summary.collections?.pending, totalCollections) },
                            { color: 'bg-error', pct: safePct(summary.collections?.overdue, totalCollections) },
                        ]} />
                        <div className="divide-y divide-border-light">
                            <StatusRow icon={CheckCircle2} label="Tahsil Edildi" value={summary.collections?.collected || 0}
                                pct={safePct(summary.collections?.collected, totalCollections)} variant="success" formatValue={formatCompact} />
                            <StatusRow icon={Clock} label="Bekliyor" value={summary.collections?.pending || 0}
                                pct={safePct(summary.collections?.pending, totalCollections)} variant="warning" formatValue={formatCompact} />
                            <StatusRow icon={AlertCircle} label="Gecikmiş" value={summary.collections?.overdue || 0}
                                pct={safePct(summary.collections?.overdue, totalCollections)} variant="error" formatValue={formatCompact} />
                        </div>
                    </Card>

                    <Card padding="md" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
                                <Building2 size={18} className="text-text-muted" /> Firma Ödemeleri
                            </h3>
                            <span className="text-sm text-text-tertiary">{formatCompact(totalPayments)}</span>
                        </div>
                        <StackedBar segments={[
                            { color: 'bg-primary', pct: safePct(summary.payments?.paid, totalPayments) },
                            { color: 'bg-primary/50', pct: safePct(summary.payments?.pending, totalPayments) },
                            { color: 'bg-primary/25', pct: safePct(summary.payments?.scheduled, totalPayments) },
                        ]} />
                        <div className="divide-y divide-border-light">
                            <StatusRow icon={CheckCircle2} label="Ödendi" value={summary.payments?.paid || 0}
                                pct={safePct(summary.payments?.paid, totalPayments)} variant="info" formatValue={formatCompact} />
                            <StatusRow icon={Clock} label="Bekliyor" value={summary.payments?.pending || 0}
                                pct={safePct(summary.payments?.pending, totalPayments)} variant="muted" formatValue={formatCompact} />
                            <StatusRow icon={CalendarCheck} label="Planlandı" value={summary.payments?.scheduled || 0}
                                pct={safePct(summary.payments?.scheduled, totalPayments)} variant="muted" formatValue={formatCompact} />
                        </div>
                    </Card>
                </div>
            )}

            {/* Alacak & Borç */}
            {showSections.receivables && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card padding="md">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
                                <ArrowDownCircle size={18} className="text-success" /> Alacaklar
                            </h3>
                            <span className="text-xs text-text-muted">{receivables.length} kişi</span>
                        </div>
                        <div className="space-y-1">
                            {receivables.slice(0, 4).map(item => (
                                <div key={item.id} className="flex items-center justify-between py-2 border-b border-border-light last:border-0">
                                    <div>
                                        <p className="text-sm font-medium text-text-primary">{item.name}</p>
                                        <p className="text-xs text-text-muted">{formatDate(item.lastPaymentDate)}</p>
                                    </div>
                                    <p className={clsx("font-semibold text-sm", item.status === 'overdue' ? 'text-error' : 'text-text-primary')}>
                                        {formatCurrency(item.totalRemaining)}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => navigate('/finans/musteriler')}
                            className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-sm text-primary hover:text-primary-dark font-medium transition-colors rounded-lg hover:bg-primary/5">
                            Tüm Müşterileri Gör <ArrowRight size={14} />
                        </button>
                    </Card>
                    <Card padding="md">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
                                <ArrowUpCircle size={18} className="text-error" /> Borçlar
                            </h3>
                            <span className="text-xs text-text-muted">{payables.length} firma</span>
                        </div>
                        <div className="space-y-1">
                            {payables.slice(0, 4).map(item => (
                                <div key={item.id} className="flex items-center justify-between py-2 border-b border-border-light last:border-0">
                                    <div>
                                        <p className="text-sm font-medium text-text-primary">{item.name}</p>
                                        <p className="text-xs text-text-muted">{formatDate(item.nextPaymentDate)}</p>
                                    </div>
                                    <p className={clsx("font-semibold text-sm", item.status === 'overdue' ? 'text-error' : 'text-text-primary')}>
                                        {formatCurrency(item.totalRemaining)}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => navigate('/finans/firmalar')}
                            className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-sm text-primary hover:text-primary-dark font-medium transition-colors rounded-lg hover:bg-primary/5">
                            Tüm Firmaları Gör <ArrowRight size={14} />
                        </button>
                    </Card>
                </div>
            )}
        </div>
    );
}
